import { useEffect, useState } from "react";
import { Loader2, KeyRound, Check, X, Trash2, ShieldCheck, Github, ArrowUp, ArrowDown, ListOrdered } from "lucide-react";
import AppNav from "@/components/AppNav";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";

const PROVIDERS = [
  { id: "anthropic", label: "Anthropic (Claude)", hint: "sk-ant-...", url: "https://console.anthropic.com/settings/keys" },
  { id: "openai", label: "OpenAI (GPT)", hint: "sk-...", url: "https://platform.openai.com/api-keys" },
  { id: "gemini", label: "Google Gemini", hint: "AIza...", url: "https://aistudio.google.com/app/apikey" },
  { id: "deepseek", label: "DeepSeek", hint: "sk-...", url: "https://platform.deepseek.com/api_keys" },
  { id: "apikeyfun", label: "apikey.fun (Claude)", hint: "sk-... (optional — a shared key is already active)", url: "https://apikey.fun" },
  { id: "apikeyfun_gpt", label: "apikey.fun · GPT", hint: "sk-...", url: "https://apikey.fun" },
  { id: "apikeyfun_gemini", label: "apikey.fun · Gemini", hint: "sk-...", url: "https://apikey.fun" },
  { id: "apikeyfun_deepseek", label: "apikey.fun · DeepSeek", hint: "sk-...", url: "https://apikey.fun" },
  { id: "crazyrouter", label: "CrazyRouter (universal router)", hint: "sk-... (optional — a shared key is already active)", url: "https://crazyrouter.com/console/token" },
  { id: "requesty", label: "Requesty (universal router)", hint: "rqsty-sk-... (optional — a shared key is already active)", url: "https://app.requesty.ai/api-keys" },
  { id: "corvus", label: "CorvusLLM (multi-model)", hint: "sk-... (optional — a shared key is already active)", url: "https://corvusllm.com" },
];

const statusBadge = {
  valid: { cls: "text-green-400 border-green-500/40", icon: Check, txt: "Valid" },
  invalid: { cls: "text-kinetic border-kinetic/40", icon: X, txt: "Invalid" },
  untested: { cls: "text-zinc-400 border-zinc-700", icon: KeyRound, txt: "Untested" },
};

/** "CrazyRouter (router universal)" → "CrazyRouter", for the compact order list. */
const shortLabel = (id) => (PROVIDERS.find((p) => p.id === id)?.label || id).split(" (")[0];

export default function Settings() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";

  const [keys, setKeys] = useState(null);
  const [inputs, setInputs] = useState({});
  const [busy, setBusy] = useState({});

  const [priority, setPriority] = useState(null);
  const [savingPriority, setSavingPriority] = useState(false);

  const [gh, setGh] = useState(null);
  const [ghPat, setGhPat] = useState("");
  const [ghBusy, setGhBusy] = useState(false);

  const load = () => api.get("/keys").then(({ data }) => setKeys(data));
  useEffect(() => {
    load();
    api.get("/github/status").then(({ data }) => setGh(data));
  }, []);

  useEffect(() => {
    if (!isAdmin) return;
    // A non-admin never sees this section, so a failure here must not block the
    // page — the keys below are what most callers came for.
    api.get("/admin/settings")
      .then(({ data }) => setPriority(data.provider_priority || []))
      .catch(() => setPriority([]));
  }, [isAdmin]);

  const movePriority = async (index, delta) => {
    const target = index + delta;
    if (!priority || target < 0 || target >= priority.length) return;

    const previous = priority;
    const next = [...priority];
    [next[index], next[target]] = [next[target], next[index]];

    // Moved optimistically: the arrows have to feel immediate, and the reorder
    // is restored from `previous` if the write is refused.
    setPriority(next);
    setSavingPriority(true);
    try {
      await api.put("/admin/settings", { provider_priority: next });
      toast.success(`${shortLabel(next[target])} now #${target + 1}`);
    } catch (err) {
      setPriority(previous);
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSavingPriority(false);
    }
  };

  const connectGh = async () => {
    if (!ghPat.trim()) return;
    setGhBusy(true);
    try {
      const { data } = await api.post("/github/connect", { pat: ghPat.trim() });
      setGh(data);
      setGhPat("");
      toast.success(`Connected as @${data.username}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setGhBusy(false);
    }
  };

  const disconnectGh = async () => {
    await api.delete("/github/disconnect");
    setGh({ connected: false });
    toast.success("GitHub disconnected");
  };

  const byProvider = (id) => (keys || []).find((k) => k.provider === id);

  const save = async (provider) => {
    const key = (inputs[provider] || "").trim();
    if (!key) return;
    setBusy((b) => ({ ...b, [provider]: "save" }));
    try {
      await api.post("/keys", { provider, key });
      setInputs((i) => ({ ...i, [provider]: "" }));
      toast.success(`${provider} key saved (encrypted)`);
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy((b) => ({ ...b, [provider]: null }));
    }
  };

  const test = async (provider) => {
    setBusy((b) => ({ ...b, [provider]: "test" }));
    try {
      const { data } = await api.post(`/keys/${provider}/test`);
      data.status === "valid" ? toast.success(`${provider} key is valid`) : toast.error(`${provider}: ${data.detail || "invalid"}`);
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy((b) => ({ ...b, [provider]: null }));
    }
  };

  const remove = async (provider) => {
    await api.delete(`/keys/${provider}`);
    toast.success(`${provider} key removed`);
    load();
  };

  return (
    <div className="min-h-screen bg-background">
      <AppNav />
      <main className="max-w-3xl mx-auto px-6 py-10">
        <div className="flex items-center gap-3 mb-2">
          <h1 className="font-app text-2xl font-semibold">API Keys</h1>
          <Badge variant="outline" className="border-border text-muted-foreground text-[10px]"><ShieldCheck className="w-3 h-3 mr-1" /> BYOK · encrypted</Badge>
        </div>
        <p className="text-sm text-muted-foreground mb-8">
          Bring your own keys. They're encrypted at rest and only used to run your generations.
        </p>

        {/* Provider priority — global, admin only */}
        {isAdmin && (
          <div className="mb-10">
            <div className="flex items-center gap-2 mb-2">
              <h2 className="font-app text-lg font-semibold">Provider priority</h2>
              <Badge variant="outline" className="border-border text-muted-foreground text-[10px]">
                <ListOrdered className="w-3 h-3 mr-1" /> admin · global
              </Badge>
            </div>
            <p className="text-sm text-muted-foreground mb-3">
              Auto mode tries providers in this order and uses the first one with a key and an
              allowed model. It applies to every generation, including those running on the
              server's shared keys.
            </p>
            <div data-testid="provider-priority" className="border border-border rounded-xl bg-card p-2">
              {priority === null ? (
                <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-zinc-600" /></div>
              ) : (
                priority.map((id, i) => (
                  <div key={id} data-testid={`priority-row-${id}`}
                    className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-background/60 transition-colors">
                    <span className="font-mono text-xs text-muted-foreground w-5">{i + 1}</span>
                    <span className="flex-1 font-app text-sm">{shortLabel(id)}</span>
                    <Button data-testid={`priority-up-${id}`} variant="outline" size="sm"
                      className="border-border h-8 w-8 p-0" aria-label={`Move ${shortLabel(id)} up`}
                      onClick={() => movePriority(i, -1)} disabled={i === 0 || savingPriority}>
                      <ArrowUp className="w-4 h-4" />
                    </Button>
                    <Button data-testid={`priority-down-${id}`} variant="outline" size="sm"
                      className="border-border h-8 w-8 p-0" aria-label={`Move ${shortLabel(id)} down`}
                      onClick={() => movePriority(i, 1)} disabled={i === priority.length - 1 || savingPriority}>
                      <ArrowDown className="w-4 h-4" />
                    </Button>
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {keys === null ? (
          <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-zinc-600" /></div>
        ) : (
          <div className="space-y-4">
            {PROVIDERS.map((p) => {
              const existing = byProvider(p.id);
              const st = statusBadge[existing?.status || "untested"];
              return (
                <div key={p.id} data-testid={`key-row-${p.id}`} className="border border-border rounded-xl bg-card p-5">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <h3 className="font-app font-semibold">{p.label}</h3>
                      <a href={p.url} target="_blank" rel="noreferrer" className="text-xs text-muted-foreground hover:text-kinetic transition-colors">Get a key →</a>
                    </div>
                    {existing && (
                      <Badge variant="outline" className={`font-mono text-[10px] ${st.cls}`}>
                        <st.icon className="w-3 h-3 mr-1" />{st.txt}
                      </Badge>
                    )}
                  </div>
                  {existing ? (
                    <div className="flex items-center gap-2">
                      <code className="flex-1 font-mono text-sm text-muted-foreground bg-background border border-border rounded-md px-3 py-2">{existing.masked}</code>
                      <Button data-testid={`test-key-${p.id}`} variant="outline" size="sm" className="border-border" onClick={() => test(p.id)} disabled={busy[p.id]}>
                        {busy[p.id] === "test" ? <Loader2 className="w-4 h-4 animate-spin" /> : "Test"}
                      </Button>
                      <Button data-testid={`remove-key-${p.id}`} variant="outline" size="sm" className="border-border text-kinetic" onClick={() => remove(p.id)}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <Input data-testid={`key-input-${p.id}`} type="password" placeholder={p.hint}
                        value={inputs[p.id] || ""} onChange={(e) => setInputs((i) => ({ ...i, [p.id]: e.target.value }))}
                        className="bg-background border-border font-mono text-sm" />
                      <Button data-testid={`save-key-${p.id}`} onClick={() => save(p.id)} disabled={busy[p.id]}
                        className="bg-white text-black hover:bg-zinc-200">
                        {busy[p.id] === "save" ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save"}
                      </Button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* GitHub connection */}
        <div className="mt-8">
          <div className="flex items-center gap-2 mb-2">
            <h2 className="font-app text-lg font-semibold">Version control</h2>
            <Badge variant="outline" className="border-border text-muted-foreground text-[10px]"><Github className="w-3 h-3 mr-1" /> GitHub</Badge>
          </div>
          <div data-testid="github-card" className="border border-border rounded-xl bg-card p-5">
            <div className="flex items-center justify-between mb-3">
              <div>
                <h3 className="font-app font-semibold">GitHub Personal Access Token</h3>
                <a href="https://github.com/settings/tokens?type=beta" target="_blank" rel="noreferrer"
                  className="text-xs text-muted-foreground hover:text-kinetic transition-colors">
                  Create a fine-grained PAT (Contents: write) →
                </a>
              </div>
              {gh?.connected && (
                <Badge variant="outline" className="font-mono text-[10px] text-green-400 border-green-500/40">
                  <Check className="w-3 h-3 mr-1" /> @{gh.username}
                </Badge>
              )}
            </div>
            {gh === null ? (
              <Loader2 className="w-4 h-4 animate-spin text-zinc-600" />
            ) : gh.connected ? (
              <Button data-testid="github-disconnect" variant="outline" size="sm" className="border-border text-kinetic" onClick={disconnectGh}>
                <Trash2 className="w-4 h-4 mr-1.5" /> Disconnect
              </Button>
            ) : (
              <div className="flex items-center gap-2">
                <Input data-testid="github-pat-input" type="password" placeholder="github_pat_..."
                  value={ghPat} onChange={(e) => setGhPat(e.target.value)}
                  className="bg-background border-border font-mono text-sm" />
                <Button data-testid="github-connect" onClick={connectGh} disabled={ghBusy}
                  className="bg-white text-black hover:bg-zinc-200">
                  {ghBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Connect"}
                </Button>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
